import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Loader, Calendar, Download, Edit, ChevronDown, CheckCircle } from 'lucide-react';
import { reportApi } from '../services/reportApi';
import { caseApi } from '../services/api';
import { httpClient } from '../services/httpClient';
import { ReportEditor } from '../components/ReportEditor';
import { EditableTitle } from '../components/EditableTitle';
import { formatDateTime } from '../utils/dateFormat';
import { useRegisterAskContext } from '../hooks/useRegisterAskContext';
import { useStructuredPiiRegistration } from '../hooks/useStructuredPiiRegistration';

export const ReportViewerPage: React.FC = () => {
  const { reportId, caseId: urlCaseId } = useParams<{ reportId: string; caseId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<string | null>(null);

  // Status update mutation
  const statusMutation = useMutation(
    (newStatus: 'complete' | 'archived') =>
      reportApi.updateReportStatus(reportId!, newStatus),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['report', reportId]);
        queryClient.invalidateQueries(['reports']);
        queryClient.invalidateQueries('recentReports'); // Invalidate dashboard reports
        setShowStatusMenu(false);
      },
      onError: (error) => {
        console.error('Failed to update status:', error);
        alert('Failed to update report status. Please try again.');
      }
    }
  );

  // Title update mutation
  const titleMutation = useMutation(
    (newTitle: string) => reportApi.updateReport(reportId!, { title: newTitle }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['report', reportId]);
        queryClient.invalidateQueries(['reports']);
        queryClient.invalidateQueries('recentReports');
      }
    }
  );

  // Close menus when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showExportMenu && !target.closest('.export-dropdown')) {
        setShowExportMenu(false);
      }
      if (showStatusMenu && !target.closest('.status-dropdown')) {
        setShowStatusMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showExportMenu, showStatusMenu]);

  const { data: report, isLoading, error } = useQuery(
    ['report', reportId],
    () => reportApi.getReport(reportId!),
    {
      enabled: !!reportId,
      // Re-poll while report is still generating
      refetchInterval: (data) => data?.status === 'generating' ? 3000 : false,
    }
  );

  // Fetch case data + metadata for structured PII registration
  const { data: caseData } = useQuery(
    ['case', report?.caseId],
    () => caseApi.getCase(report!.caseId),
    { enabled: !!report?.caseId }
  );

  const { data: metadata } = useQuery(
    ['metadata', report?.caseId],
    async () => {
      try {
        return await httpClient.get<any>(`/intelligence/metadata/${report!.caseId}`);
      } catch { return null; }
    },
    { enabled: !!report?.caseId }
  );

  useStructuredPiiRegistration(caseData || undefined, metadata?.patients);

  // Register context with the unified Ask sidebar
  useRegisterAskContext({
    pageType: 'report-viewer',
    caseId: report?.caseId,
    reportId: reportId || undefined,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'generating':
        return 'bg-purple-100 text-purple-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'generating':
        return 'Generating\u2026';
      case 'failed':
        return 'Failed';
      case 'archived':
        return 'Archived';
      default:
        return 'Complete';
    }
  };

  const handleStatusChange = (newStatus: 'complete' | 'archived') => {
    if (newStatus === 'archived' && report?.status !== 'archived') {
      if (!confirm('Archive this report? Archived reports are hidden from active lists.')) {
        return;
      }
    }

    statusMutation.mutate(newStatus);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-2 text-medical-600">Loading report...</span>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-medical-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-medical-900 mb-2">Draft not found</h3>
            <p className="text-medical-600 mb-4">The draft you're looking for doesn't exist or you don't have access to it.</p>
            <Link to="/drafts" className="btn-primary">
              Back to Drafts
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(urlCaseId ? `/cases/${urlCaseId}/drafts` : '/drafts')}
          className="flex items-center gap-2 text-medical-600 hover:text-medical-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Drafts
        </button>

        <EditableTitle
          title={report.title}
          onSave={async (newTitle) => {
            await titleMutation.mutateAsync(newTitle);
          }}
          className="text-2xl font-bold text-medical-900 dark:text-medical-100"
          placeholder="Enter draft title..."
        />

        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-4 text-sm text-medical-600">
            {/* Status Dropdown */}
            <div className="relative status-dropdown">
              <button
                onClick={() => setShowStatusMenu(!showStatusMenu)}
                disabled={statusMutation.isLoading}
                className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 hover:opacity-80 transition-opacity ${getStatusColor(report.status)}`}
              >
                {getStatusLabel(report.status)}
                <ChevronDown className="h-3 w-3" />
              </button>

              {showStatusMenu && (
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-medical-200 py-1 z-10">
                  <button
                    onClick={() => handleStatusChange('complete')}
                    disabled={statusMutation.isLoading}
                    className={`w-full text-left px-4 py-2 hover:bg-medical-50 transition-colors text-sm ${
                      report.status === 'complete' ? 'bg-green-50 font-medium' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {report.status === 'complete' && <CheckCircle className="h-4 w-4 text-green-600" />}
                      <div>
                        <div className="text-medical-900">Complete</div>
                        <div className="text-xs text-medical-500">Active draft</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleStatusChange('archived')}
                    disabled={statusMutation.isLoading}
                    className={`w-full text-left px-4 py-2 hover:bg-medical-50 transition-colors text-sm ${
                      report.status === 'archived' ? 'bg-gray-50 font-medium' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {report.status === 'archived' && <CheckCircle className="h-4 w-4 text-gray-600" />}
                      <div>
                        <div className="text-medical-900">Archived</div>
                        <div className="text-xs text-medical-500">No longer active</div>
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>Created {formatDateTime(report.createdAt)}</span>
            </div>
            {report.updatedAt !== report.createdAt && (
              <span>• Updated {formatDateTime(report.updatedAt)}</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(urlCaseId ? `/cases/${urlCaseId}/drafts/${report.id}/edit` : `/drafts/${report.id}/edit`)}
              className="btn-secondary flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit
            </button>

            {/* Export Dropdown */}
            <div className="relative export-dropdown">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="btn-secondary flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Export
                <ChevronDown className="h-3 w-3" />
              </button>

              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-medical-200 py-1 z-10">
                  <button
                    disabled={exportingFormat !== null}
                    onClick={async () => {
                      try {
                        setExportingFormat('txt');
                        await reportApi.exportReport(report.id, 'txt');
                        setShowExportMenu(false);
                      } catch (error) {
                        console.error('Export failed:', error);
                        alert('Failed to export report. Please try again.');
                      } finally {
                        setExportingFormat(null);
                      }
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-medical-50 transition-colors text-sm text-medical-900"
                  >
                    <div className="flex items-center gap-2">
                      {exportingFormat === 'txt' ? <Loader className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                      <div>
                        <div className="font-medium">Text (.txt)</div>
                        <div className="text-xs text-medical-500">Plain text format</div>
                      </div>
                    </div>
                  </button>

                  <button
                    disabled={exportingFormat !== null}
                    onClick={async () => {
                      try {
                        setExportingFormat('docx');
                        await reportApi.exportReport(report.id, 'docx');
                        setShowExportMenu(false);
                      } catch (error) {
                        console.error('Export failed:', error);
                        alert('Failed to export report. Please try again.');
                      } finally {
                        setExportingFormat(null);
                      }
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-medical-50 transition-colors text-sm text-medical-900"
                  >
                    <div className="flex items-center gap-2">
                      {exportingFormat === 'docx' ? <Loader className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                      <div>
                        <div className="font-medium">Word (.docx)</div>
                        <div className="text-xs text-medical-500">Microsoft Word format</div>
                      </div>
                    </div>
                  </button>

                  <button
                    disabled={exportingFormat !== null}
                    onClick={async () => {
                      try {
                        setExportingFormat('pdf');
                        await reportApi.exportReport(report.id, 'pdf');
                        setShowExportMenu(false);
                      } catch (error) {
                        console.error('Export failed:', error);
                        alert('Failed to export report. Please try again.');
                      } finally {
                        setExportingFormat(null);
                      }
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-medical-50 transition-colors text-sm text-medical-900"
                  >
                    <div className="flex items-center gap-2">
                      {exportingFormat === 'pdf' ? <Loader className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                      <div>
                        <div className="font-medium">PDF</div>
                        <div className="text-xs text-medical-500">Portable document format</div>
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Generation Prompt */}
      {report.generationPrompt && (
        <div className="card bg-cyan-50 border-cyan-200 mb-6">
          <h3 className="text-sm font-medium text-cyan-900 mb-2">Generation Instructions</h3>
          <p className="text-sm text-cyan-700">{report.generationPrompt}</p>
        </div>
      )}

      {/* Draft Content */}
      {report.status === 'generating' ? (
        <div className="card bg-white shadow-sm">
          <div className="flex flex-col items-center justify-center py-16">
            <Loader className="h-8 w-8 animate-spin text-primary-600 mb-4" />
            <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-2">Generating draft...</h3>
            <p className="text-sm text-medical-600 dark:text-medical-400">This may take a minute. The page will update automatically.</p>
          </div>
        </div>
      ) : (
        <div className="card bg-white shadow-sm">
          <div className="max-w-4xl mx-auto">
            <ReportEditor content={!report.content ? '' : typeof report.content === 'string' ? report.content : JSON.stringify(report.content)} readOnly={true} />
          </div>
        </div>
      )}

      {/* Case Link — only show when viewing from global reports page */}
      {!urlCaseId && (
        <div className="mt-6">
          <Link
            to={`/cases/${report.caseId}`}
            className="text-primary-600 hover:text-primary-700 flex items-center gap-2"
          >
            <FileText className="h-4 w-4" />
            View Case Details
          </Link>
        </div>
      )}
    </div>
  );
};
